import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Core access: may this signed-in member use the core app (Prompt of the Day,
 * Freestyle, the native app shell)? The server decides through
 * `get_my_core_access`; the client never computes the cutoff itself.
 *
 * - `full`: Premium in any form, or the gate is not active for this account
 *   (today's behavior until the release-day cutoff is set).
 * - `legacy_free`: the account existed before the cutoff (or has a manual
 *   grant), so it keeps the free features it had.
 * - `none`: the gate is active and the account has neither.
 * - `error`: the server could not answer. Never treated as `none`, so a
 *   legacy member never sees the paywall because of a network failure.
 * - `loading`: auth or the first answer is still loading. Signed-out
 *   visitors also report `loading` with `signedOut: true`; core access does
 *   not apply to them, so callers check `signedOut` first.
 */
export type CoreAccessStatus = 'loading' | 'error' | 'full' | 'legacy_free' | 'none';

interface CoreAccessAnswer {
  premium: boolean;
  legacyFree: boolean;
  gateActive: boolean;
}

interface CoreAccessState {
  status: CoreAccessStatus;
  gateActive: boolean;
  signedOut: boolean;
  refetch: () => Promise<unknown>;
}

const CORE_ACCESS_QUERY_ROOT = 'core-access' as const;

export function coreAccessQueryKey(userId: string | null | undefined) {
  return [CORE_ACCESS_QUERY_ROOT, userId ?? null] as const;
}

/** Refetch every member's cached core access (after a purchase, restore or checkout return). */
export function invalidateCoreAccess(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [CORE_ACCESS_QUERY_ROOT] });
}

function parseCoreAccess(data: unknown): CoreAccessAnswer {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('get_my_core_access returned an unexpected shape');
  }
  const row = data as Record<string, unknown>;
  if (
    typeof row.premium !== 'boolean' ||
    typeof row.legacy_free !== 'boolean' ||
    typeof row.gate_active !== 'boolean'
  ) {
    throw new Error('get_my_core_access returned an unexpected shape');
  }
  return {
    premium: row.premium,
    legacyFree: row.legacy_free,
    gateActive: row.gate_active,
  };
}

function resolveCoreAccessStatus(answer: CoreAccessAnswer): Exclude<CoreAccessStatus, 'loading' | 'error'> {
  if (answer.premium) return 'full';
  if (!answer.gateActive) return 'full';
  if (answer.legacyFree) return 'legacy_free';
  return 'none';
}

/**
 * PostgREST "function not found" (PGRST202) or Postgres undefined_function
 * (42883): the server predates the gate (the minutes between a web deploy and
 * db:migrate, or a preview database), so the gate cannot be active.
 */
export function isMissingCoreAccessFunction(error: { code?: string | null } | null | undefined): boolean {
  return error?.code === 'PGRST202' || error?.code === '42883';
}

async function fetchCoreAccess(): Promise<CoreAccessAnswer> {
  const { data, error } = await supabase.rpc('get_my_core_access');
  if (error) {
    if (isMissingCoreAccessFunction(error)) return { premium: false, legacyFree: true, gateActive: false };
    throw error;
  }
  return parseCoreAccess(data);
}

export function useCoreAccess(): CoreAccessState {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: coreAccessQueryKey(userId),
    queryFn: fetchCoreAccess,
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
  });

  const refetch = query.refetch;

  if (authLoading) {
    return { status: 'loading', gateActive: false, signedOut: false, refetch };
  }
  if (!userId) {
    return { status: 'loading', gateActive: false, signedOut: true, refetch };
  }
  // A cached answer wins over a failed background refresh.
  if (query.data) {
    return {
      status: resolveCoreAccessStatus(query.data),
      gateActive: query.data.gateActive,
      signedOut: false,
      refetch,
    };
  }
  if (query.isError) {
    return { status: 'error', gateActive: false, signedOut: false, refetch };
  }
  return { status: 'loading', gateActive: false, signedOut: false, refetch };
}

/**
 * Keeps core access in step with the signed-in account: on sign-in, sign-out
 * or an account switch, drop the previous account's answer and refetch.
 * Keyed on the user id, never on the user object (it churns identity on
 * token refresh).
 */
export function useCoreAccessAuthSync(): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (previousUserId === undefined || previousUserId === userId) return;
    if (previousUserId) {
      queryClient.removeQueries({ queryKey: coreAccessQueryKey(previousUserId), exact: true });
    }
    void invalidateCoreAccess(queryClient);
  }, [queryClient, userId]);
}

/** Mount once inside the auth and query providers. Renders nothing. */
export function CoreAccessAuthSync(): null {
  useCoreAccessAuthSync();
  return null;
}
